import { useEffect, useState, useCallback } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Plus, ListTodo, Pencil, Trash2, Check, SkipForward, Save, X, Loader2, Flag } from "lucide-react";

import { API } from "@/lib/api";
import { readToken } from "@/lib/tokenStore";

const TASK_TYPES = [
  { value: "follow_up", label: "Follow-up" },
  { value: "call", label: "Anrufen" },
  { value: "email", label: "Mail senden" },
  { value: "meeting_prep", label: "Termin vorbereiten" },
  { value: "request_documents", label: "Unterlagen anfordern" },
  { value: "review_documents", label: "Unterlagen prüfen" },
  { value: "prepare_offer", label: "Angebot vorbereiten" },
  { value: "internal_review", label: "Interne Prüfung" },
  { value: "partner_review", label: "Partner prüfen" },
  { value: "dev_review", label: "Dev-Einreichung prüfen" },
  { value: "other", label: "Sonstiges" },
];
const TYPE_LABEL = Object.fromEntries(TASK_TYPES.map((o) => [o.value, o.label]));

const PRIORITIES = [
  { value: "low", label: "Niedrig" },
  { value: "normal", label: "Normal" },
  { value: "high", label: "Hoch" },
  { value: "urgent", label: "Dringend" },
];
const PRIO_LABEL = Object.fromEntries(PRIORITIES.map((o) => [o.value, o.label]));
const PRIO_TONE = {
  low: "text-zinc-400",
  normal: "text-zinc-300",
  high: "text-amber-300",
  urgent: "text-red-300",
};

const STATUS_TONE = {
  open: "border-emerald-500/30 bg-emerald-500/10 text-emerald-300",
  done: "border-zinc-600/40 bg-zinc-700/20 text-zinc-300",
  skipped: "border-amber-500/30 bg-amber-500/10 text-amber-300",
};
const STATUS_LABEL = { open: "offen", done: "erledigt", skipped: "übersprungen" };

const inputClass = "w-full rounded border border-white/10 bg-[#0B0F10] px-3 py-2 text-sm text-zinc-100 outline-none transition focus:border-emerald-500/40";

function headers() {
  const t = readToken();
  return t ? { Authorization: `Bearer ${t}` } : {};
}

function fmt(iso) {
  if (!iso) return "—";
  try {
    return new Date(iso).toLocaleString("de-DE", {
      day: "2-digit", month: "2-digit", year: "numeric",
      hour: "2-digit", minute: "2-digit",
    });
  } catch { return iso; }
}

function isOverdue(iso) {
  if (!iso) return false;
  try { return new Date(iso).getTime() < Date.now(); } catch { return false; }
}

const EMPTY_FORM = {
  title: "", description: "", due_at: "",
  type: "follow_up", priority: "normal",
};

export default function TasksTab({ leadId, onChange, autoOpenForm }) {
  const [tasks, setTasks] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [form, setForm] = useState(EMPTY_FORM);
  const [saving, setSaving] = useState(false);
  const [editId, setEditId] = useState(null);
  const [editForm, setEditForm] = useState(EMPTY_FORM);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const r = await axios.get(`${API}/admin/crm/leads/${leadId}/tasks`, {
        headers: headers(), timeout: 15000,
      });
      setTasks(r.data?.items || []);
    } catch { setTasks([]); }
    finally { setLoading(false); }
  }, [leadId]);

  useEffect(() => { load(); }, [load]);
  useEffect(() => { if (autoOpenForm) setShowForm(true); }, [autoOpenForm]);

  const update = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));
  const updateE = (k) => (e) => setEditForm((f) => ({ ...f, [k]: e.target.value }));

  const submit = async () => {
    if (!form.title.trim()) return;
    setSaving(true);
    try {
      const payload = Object.fromEntries(
        Object.entries(form).filter(([, v]) => v)
      );
      if (payload.due_at) payload.due_at = new Date(payload.due_at).toISOString();
      await axios.post(`${API}/admin/crm/leads/${leadId}/tasks`, payload, {
        headers: headers(), timeout: 15000,
      });
      toast.success("Aufgabe angelegt");
      setForm(EMPTY_FORM); setShowForm(false);
      await load(); onChange?.();
    } catch (err) {
      toast.error(err?.response?.data?.detail?.[0]?.msg || "Speichern fehlgeschlagen");
    } finally { setSaving(false); }
  };

  const startEdit = (t) => {
    setEditId(t.id);
    setEditForm({
      title: t.title || "", description: t.description || "",
      due_at: t.due_at ? new Date(t.due_at).toISOString().slice(0, 16) : "",
      type: t.type || "follow_up", priority: t.priority || "normal",
    });
  };

  const saveEdit = async () => {
    setSaving(true);
    try {
      const payload = {
        title: editForm.title.trim(),
        description: editForm.description.trim() || null,
        due_at: editForm.due_at ? new Date(editForm.due_at).toISOString() : null,
        type: editForm.type, priority: editForm.priority,
      };
      await axios.patch(`${API}/admin/crm/tasks/${editId}`, payload, {
        headers: headers(), timeout: 15000,
      });
      toast.success("Aufgabe aktualisiert");
      setEditId(null);
      await load(); onChange?.();
    } catch (err) {
      toast.error("Speichern fehlgeschlagen");
    } finally { setSaving(false); }
  };

  const setStatus = async (id, status) => {
    try {
      await axios.patch(`${API}/admin/crm/tasks/${id}`, { status }, { headers: headers() });
      toast.success(status === "done" ? "Aufgabe erledigt" : "Aufgabe übersprungen");
      await load(); onChange?.();
    } catch {
      toast.error("Aktion fehlgeschlagen");
    }
  };

  const remove = async (id) => {
    if (!window.confirm("Aufgabe wirklich löschen?")) return;
    try {
      await axios.delete(`${API}/admin/crm/tasks/${id}`, { headers: headers() });
      toast.success("Aufgabe gelöscht");
      await load(); onChange?.();
    } catch {
      toast.error("Löschen fehlgeschlagen");
    }
  };

  return (
    <section data-testid="journey-tasks-tab" className="space-y-4">
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-2 text-sm font-semibold text-zinc-100">
          <ListTodo className="h-4 w-4 text-emerald-400" />
          Aufgaben ({tasks.length})
        </div>
        {!showForm ? (
          <button type="button" onClick={() => setShowForm(true)} data-testid="tasks-add-button"
            className="inline-flex items-center gap-1 rounded bg-emerald-500 px-3 py-1.5 text-xs font-medium text-zinc-950 hover:bg-emerald-400">
            <Plus className="h-3.5 w-3.5" />Aufgabe anlegen
          </button>
        ) : null}
      </div>

      {showForm ? (
        <div data-testid="tasks-form" className="space-y-3 rounded-md border border-white/10 bg-white/[0.02] p-4">
          <input type="text" value={form.title} onChange={update("title")} placeholder="Titel"
            data-testid="tasks-form-title" className={inputClass} autoFocus />
          <textarea value={form.description} onChange={update("description")}
            placeholder="Beschreibung (optional)" className={`${inputClass} min-h-[60px]`} />
          <div className="grid gap-3 sm:grid-cols-3">
            <select value={form.type} onChange={update("type")} className={inputClass} data-testid="tasks-form-type">
              {TASK_TYPES.map((o) => <option key={o.value} value={o.value}>{o.label}</option>)}
            </select>
            <select value={form.priority} onChange={update("priority")} className={inputClass} data-testid="tasks-form-priority">
              {PRIORITIES.map((o) => <option key={o.value} value={o.value}>{o.label}</option>)}
            </select>
            <input type="datetime-local" value={form.due_at} onChange={update("due_at")}
              data-testid="tasks-form-due" className={inputClass} />
          </div>
          <div className="flex justify-end gap-2">
            <button type="button" onClick={() => { setShowForm(false); setForm(EMPTY_FORM); }} className="rounded border border-white/10 px-3 py-1.5 text-xs text-zinc-300 hover:bg-white/5">
              Abbrechen
            </button>
            <button type="button" disabled={saving || !form.title.trim()} onClick={submit} data-testid="tasks-form-save"
              className="inline-flex items-center gap-1.5 rounded bg-emerald-500 px-3 py-1.5 text-xs font-medium text-zinc-950 hover:bg-emerald-400 disabled:opacity-40">
              {saving ? <Loader2 className="h-3 w-3 animate-spin" /> : <Save className="h-3 w-3" />}
              Aufgabe speichern
            </button>
          </div>
        </div>
      ) : null}

      {loading ? (
        <div className="space-y-2">
          {Array.from({ length: 3 }).map((_, i) => <div key={i} className="h-16 animate-pulse rounded bg-white/[0.03]" />)}
        </div>
      ) : tasks.length === 0 ? (
        <p className="text-sm text-zinc-500">Keine Aufgaben vorhanden.</p>
      ) : (
        <ul className="space-y-2">
          {tasks.map((t) => {
            const overdue = t.status === "open" && isOverdue(t.due_at);
            return (
              <li key={t.id} data-testid={`task-${t.id}`}
                className={`space-y-2 rounded-md border p-4 ${overdue ? "border-red-500/30 bg-red-500/[0.04]" : "border-white/[0.06] bg-white/[0.01]"}`}>
                <div className="flex items-start justify-between gap-3">
                  <div className="flex-1 space-y-1">
                    <div className="flex flex-wrap items-center gap-2 text-[11px]">
                      <span className={`inline-flex items-center rounded border px-2 py-0.5 ${STATUS_TONE[t.status]}`}>
                        {STATUS_LABEL[t.status] || t.status}
                      </span>
                      <span className="text-zinc-500">{TYPE_LABEL[t.type] || t.type}</span>
                      <span className={`inline-flex items-center gap-0.5 ${PRIO_TONE[t.priority] || ""}`}>
                        <Flag className="h-3 w-3" />{PRIO_LABEL[t.priority] || t.priority}
                      </span>
                      {t.due_at ? (
                        <span className={overdue ? "text-red-300" : "text-zinc-400"}>
                          Fällig {fmt(t.due_at)}
                        </span>
                      ) : null}
                    </div>
                    {editId === t.id ? (
                      <div className="space-y-2 pt-2">
                        <input type="text" value={editForm.title} onChange={updateE("title")} className={inputClass} />
                        <textarea value={editForm.description} onChange={updateE("description")} className={`${inputClass} min-h-[60px]`} />
                        <div className="grid gap-2 sm:grid-cols-3">
                          <select value={editForm.type} onChange={updateE("type")} className={inputClass}>
                            {TASK_TYPES.map((o) => <option key={o.value} value={o.value}>{o.label}</option>)}
                          </select>
                          <select value={editForm.priority} onChange={updateE("priority")} className={inputClass}>
                            {PRIORITIES.map((o) => <option key={o.value} value={o.value}>{o.label}</option>)}
                          </select>
                          <input type="datetime-local" value={editForm.due_at} onChange={updateE("due_at")} className={inputClass} />
                        </div>
                        <div className="flex justify-end gap-2">
                          <button type="button" onClick={() => setEditId(null)} className="rounded border border-white/10 px-3 py-1 text-xs text-zinc-300">
                            <X className="mr-1 inline h-3 w-3" />Abbrechen
                          </button>
                          <button type="button" disabled={saving} onClick={saveEdit} className="inline-flex items-center gap-1 rounded bg-emerald-500 px-3 py-1 text-xs font-medium text-zinc-950 hover:bg-emerald-400">
                            <Save className="h-3 w-3" />Speichern
                          </button>
                        </div>
                      </div>
                    ) : (
                      <>
                        <div className="text-sm font-medium text-zinc-100">{t.title}</div>
                        {t.description ? <p className="text-xs text-zinc-400">{t.description}</p> : null}
                      </>
                    )}
                  </div>
                  {editId !== t.id && t.status === "open" ? (
                    <div className="flex shrink-0 flex-col gap-1 text-zinc-400">
                      <button type="button" onClick={() => setStatus(t.id, "done")} data-testid={`task-done-${t.id}`} title="Erledigen" className="rounded p-1 hover:bg-emerald-500/10 hover:text-emerald-300">
                        <Check className="h-3.5 w-3.5" />
                      </button>
                      <button type="button" onClick={() => setStatus(t.id, "skipped")} data-testid={`task-skip-${t.id}`} title="Überspringen" className="rounded p-1 hover:bg-amber-500/10 hover:text-amber-300">
                        <SkipForward className="h-3.5 w-3.5" />
                      </button>
                      <button type="button" onClick={() => startEdit(t)} data-testid={`task-edit-${t.id}`} title="Bearbeiten" className="rounded p-1 hover:bg-white/5 hover:text-zinc-100">
                        <Pencil className="h-3.5 w-3.5" />
                      </button>
                      <button type="button" onClick={() => remove(t.id)} data-testid={`task-delete-${t.id}`} title="Löschen" className="rounded p-1 hover:bg-red-500/10 hover:text-red-300">
                        <Trash2 className="h-3.5 w-3.5" />
                      </button>
                    </div>
                  ) : null}
                </div>
              </li>
            );
          })}
        </ul>
      )}
    </section>
  );
}
